import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { slideInFromLeftWeighted } from '../../shared/component/Animation';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { paperTextureModal } from '../../shared/component/paperTexture';
import { getBtnName } from '../../shared/constants/btnNames';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useScrollContainerMarqueeSelect } from '../../shared/hooks/useScrollContainerMarqueeSelect';
import { useCanShowProjectDetail } from '../../shared/hooks/useProjectVisibility';
import { SelectedData, WorkDetailSimpleData, WorkSimpleWithDetails } from '../../shared/dto/EntityRepository';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalWorkTagStore } from '../../shared/store/workTagStore';
import { resolveUploadFileOriginUrl } from '../../shared/util/normalizeWorkData';
import { CAROUSEL_MAX_ITEMS } from '../../shared/util/carouselUtils';

import ImportTagStation from './ImportTagStation';
import PageAboutWorkImport from './PageAboutWorkImport';
import CarouselImportSelectionSlots from './CarouselImportSelectionSlots';
import SimpleWorkForFormationItem from './SimpleWorkForFormationItem';
import { clearFormationSlotImportModalState } from './useFormationSlotImport';
import { useCarouselSlotImport } from './useCarouselSlotImport';

const ImportStationForCarousel: React.FC = () => {
  const { isClosing, clearModal } = useModalStore();
  const { simpleList } = useImportStationWorkStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();
  const { selectedModalTags } = useModalWorkTagStore();
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);
  const listRef = useRef<HTMLDivElement>(null);
  const {
    capacity,
    importSelections,
    isAlreadyInCarousel,
    isBulkImport,
    items,
    occupiedCount,
    replaceSlotIndex,
  } = useCarouselSlotImport();

  useEffect(() => () => {
    clearFormationSlotImportModalState();
  }, []);

  const handleCancel = () => {
    clearFormationSlotImportModalState();
    clearModal();
  };

  const marqueeTargets = useMemo(() => {
    const rows: Array<
      | { id: string; kind: 'work'; work: WorkSimpleWithDetails }
      | { id: string; kind: 'detail'; work: WorkSimpleWithDetails; detail: WorkDetailSimpleData }
    > = [];
    simpleList.forEach((work) => {
      rows.push({ id: `work:${work.id}`, kind: 'work', work });
      work.detailList.forEach((detail) => {
        rows.push({ id: `detail:${detail.id}`, kind: 'detail', work, detail });
      });
    });
    return rows;
  }, [simpleList]);

  const toSelectedData = (
    work: WorkSimpleWithDetails,
    detail?: WorkDetailSimpleData,
  ): SelectedData => ({
    workId: work.id,
    ...(detail ? { detailId: detail.id } : {}),
    originUrl: resolveUploadFileOriginUrl(detail?.uploadFile ?? work.uploadFile),
    title: work.title,
    prodYear: work.prodYear,
    material: work.material,
    size: work.size,
    sizeText: work.sizeText,
    dimensionsType: work.dimensionsType,
    duration: work.duration,
    artist: work.artist,
  });

  const commitMarquee = useCallback((hit: typeof marqueeTargets) => {
    if (hit.length === 0) return;
    let showedAlert = false;
    hit.forEach((row) => {
      const importStore = useImportStationWorkStore.getState();
      const selected = toSelectedData(row.work, row.kind === 'detail' ? row.detail : undefined);
      if (isAlreadyInCarousel(selected)) return;
      const alreadySelected = importStore.selectedDataList.some((item) => (
        item.workId === selected.workId && item.detailId === selected.detailId
      ));
      if (alreadySelected) return;
      if (importStore.selectedDataList.length >= capacity) {
        if (!showedAlert) {
          enqueueAlert({
            type: AlertType.ALERT,
            position: AlertPosition.TOP,
            content: getAlertMessage('CAROUSEL_IMPORT_MAX', { maxCount: CAROUSEL_MAX_ITEMS }),
          });
          showedAlert = true;
        }
        return;
      }
      importStore.addSelectedData({
        ...selected,
        sourceWork: row.work,
        sourceDetail: row.kind === 'detail' ? row.detail : undefined,
      });
    });
  }, [capacity, enqueueAlert, isAlreadyInCarousel]);

  const getMarqueeItemId = useCallback((item: (typeof marqueeTargets)[number]) => item.id, []);
  const getMarqueeRowId = useCallback((element: HTMLElement) => element.dataset.importRowId, []);
  const {
    handleContainerMouseDown,
    handleContainerClickCapture,
    marqueeBox,
    marqueeSelecting,
    hitItems,
  } = useScrollContainerMarqueeSelect({
    items: marqueeTargets,
    getItemId: getMarqueeItemId,
    rowSelector: '[data-import-row-id]',
    getRowIdFromElement: getMarqueeRowId,
    containerRef: listRef,
    enabled: isBulkImport && simpleList.length > 0,
    onCommit: commitMarquee,
  });
  const hitIdSet = useMemo(() => new Set(hitItems.map((item) => item.id)), [hitItems]);

  if (!canShowProject || !project) return null;

  return (
    <LeftModalFrame
      onClick={(event) => event.stopPropagation()}
      $isClosing={isClosing}
    >
      <ImportComp>
        <ImportMainTitleWrapper>
          <ImportTitle>
            Carousel | {getModalMessage('SELECT_TO_IMPORT_WORK_DETAIL')}
            {` (${occupiedCount}/${CAROUSEL_MAX_ITEMS})`}
          </ImportTitle>
        </ImportMainTitleWrapper>
        <CarouselImportSelectionSlots
          items={items}
          replaceSlotIndex={replaceSlotIndex}
        />
        <ImportTagStation />
        <ImportContentContainer
          ref={listRef}
          onMouseDown={handleContainerMouseDown}
          onClickCapture={handleContainerClickCapture}
          $marqueeSelecting={marqueeSelecting}
        >
          {simpleList.length === 0 ? (
            <NoContentContainer>
              {selectedModalTags.length > 0
                ? getPageMessage('IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS')
                : getPageMessage('IMPORT_STATION_EMPTY_WORK')}
            </NoContentContainer>
          ) : (
            simpleList.map((work) => (
              <SimpleContentComp key={work.id}>
                <SimpleWorkForFormationItem data={work} marqueeHitIds={hitIdSet} />
              </SimpleContentComp>
            ))
          )}
          {marqueeBox ? (
            <MarqueeSelectionBox
              style={{
                left: marqueeBox.left,
                top: marqueeBox.top,
                width: marqueeBox.width,
                height: marqueeBox.height,
              }}
            />
          ) : null}
        </ImportContentContainer>
        <PageAboutWorkImport />
        {isBulkImport && (
          <ButtonContainer>
            {capacity > 0 && (
              <HeadlessBtn
                value={getBtnName('IMPORT')}
                handleClick={importSelections}
                StyledBtn={BlackFitBtn}
              />
            )}
            <HeadlessBtn
              value={getBtnName('CANCEL')}
              handleClick={handleCancel}
              StyledBtn={PaperBlurFitBtn}
            />
          </ButtonContainer>
        )}
      </ImportComp>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  left: 4vw;
  width: calc(40vw + 40px);
  min-width: 500px;
  max-width: 600px;
  height: 75vh;
  padding: 20px;
  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'translateX(-100%)' : 'translateX(0)')};
  transition: all 0.6s ease-out;
  ${paperTextureModal}
  ${slideInFromLeftWeighted}
`;

const ImportComp = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
`;

const ImportMainTitleWrapper = styled.div`
  width: 100%;
  height: 5vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const ImportTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ImportContentContainer = styled.div<{ $marqueeSelecting?: boolean }>`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: scroll;
  user-select: ${({ $marqueeSelecting }) => ($marqueeSelecting ? 'none' : 'auto')};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 2;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
  white-space: pre-line;
`;

const SimpleContentComp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export default ImportStationForCarousel;
